import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Package, Mail, ArrowRight, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PaymentDetails {
  status: string;
  amount: number;
  currency: string;
  metadata: {
    customer_name?: string;
    customer_email?: string;
    items?: string;
  };
}

const Success = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const paymentIntentId = searchParams.get('payment_intent');
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      if (!paymentIntentId) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${import.meta.env.VITE_BACKEND_URL || 'https://snatchedlatinas.ch/api'}/payment-status/${paymentIntentId}`
        );
        
        if (response.ok) {
          const data = await response.json();
          setPaymentDetails(data);
        }
      } catch (error) {
        console.error('Erro ao buscar detalhes do pagamento:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [paymentIntentId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-green-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#4ECDC4]"></div>
      </div>
    );
  }

  const parseItems = () => {
    try {
      return paymentDetails?.metadata?.items ? JSON.parse(paymentDetails.metadata.items) : [];
    } catch {
      return [];
    }
  };

  const items = parseItems();

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-green-50 py-12">
      <div className="max-w-3xl mx-auto px-4">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-6">
            <CheckCircle className="w-16 h-16 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('success.title')}
          </h1>
          <p className="text-lg text-gray-600">
            {t('success.subtitle')}
          </p>
        </div>

        {/* Payment Details Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6 border border-gray-100">
          <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{t('success.orderDetails')}</h2>
              {paymentIntentId && (
                <p className="text-sm text-gray-500 mt-1">{t('success.orderId')}: {paymentIntentId.substring(0, 20)}...</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-[#4ECDC4]">
                CHF {paymentDetails ? (paymentDetails.amount / 100).toFixed(2) : '0.00'}
              </p>
              <p className="text-sm text-gray-500 mt-1">{t('success.paid')}</p>
            </div>
          </div>

          {/* Customer Info */}
          {paymentDetails?.metadata && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <Mail className="w-5 h-5 mr-2 text-[#4ECDC4]" />
                {t('success.customerInfo')}
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                {paymentDetails.metadata.customer_name && (
                  <p className="text-gray-700">
                    <span className="font-medium">{t('success.name')}:</span> {paymentDetails.metadata.customer_name}
                  </p>
                )}
                {paymentDetails.metadata.customer_email && (
                  <p className="text-gray-700">
                    <span className="font-medium">Email:</span> {paymentDetails.metadata.customer_email}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Items Purchased */}
          {items.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <Package className="w-5 h-5 mr-2 text-[#4ECDC4]" />
                {t('success.orderSummary')}
              </h3>
              <div className="space-y-3">
                {items.map((item: any, index: number) => (
                  <div key={index} className="flex justify-between items-center bg-gray-50 rounded-lg p-4">
                    <div>
                      <p className="font-medium text-gray-900">{item.name}</p>
                      <p className="text-sm text-gray-600">{t('success.quantity')}: {item.quantity}</p>
                    </div>
                    <p className="font-bold text-gray-900">
                      CHF {((item.price || 0) * item.quantity).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Next Steps */}
          <div className="bg-gradient-to-r from-[#4ECDC4]/10 to-[#95C11F]/10 rounded-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-3">📧 {t('success.whatNext')}</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-[#4ECDC4] mr-2">✓</span>
                <span>{t('success.step1Desc')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#4ECDC4] mr-2">✓</span>
                <span>{t('success.step2Desc')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#4ECDC4] mr-2">✓</span>
                <span>{t('success.step3Desc')}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/"
            className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] text-white rounded-xl font-bold text-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
          >
            <Home className="w-5 h-5 mr-2" />
            {t('success.backToHome')}
          </Link>
          <Link
            to="/produtos"
            className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#4ECDC4] border-2 border-[#4ECDC4] rounded-xl font-bold text-lg hover:bg-[#4ECDC4] hover:text-white transition-all duration-200"
          >
            {t('success.continueShopping')}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>

        {/* Support Info */}
        <div className="text-center mt-8 text-gray-600">
          <p className="text-sm">
            {t('success.needHelp')}{' '}
            <a href="mailto:adm.snatchedlatinas@gmail.com" className="text-[#4ECDC4] hover:underline font-medium">
              {t('success.contactUs')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Success;
